import { useMemo, useRef, useState } from 'react'

import { Rule } from 'antd/lib/form'
import styled, { css } from 'styled-components'

import { getBlockConfig } from '../templateEditor/BlockConfigs'
import { CaptureBlock as CaptureBlockType } from '../types/blueprint'

import { AddAnother } from './AddAnother'
import { CaptureAttachments } from './CaptureAttachments'
import { RangePickerProps } from './CaptureBlocks/DateTimeCaptureBlock'
import { CaptureDefects } from './CaptureDefects'
import { CaptureNotes, NotesEditor } from './CaptureNotes'
import { CapturePlatformActions } from './CapturePlatformActions'
import { DisplayAttachmentsWrapper } from './DisplayBlock.style'
import { DisplayFormDefects } from './DisplayDefects'
import { DisplayFormNotes } from './DisplayNotes'
import { DisplayFormPlatformActions } from './DisplayPlatformActions'
import { getNotesId } from './utils/mapValuesToTemplate'

import { SignatureData } from '@/components/form-components/SignatureAttachmentUploader'
import { useTemplateRenderer } from '@/context/templateRendererContext'

const CaptureBlockWrapper = styled.div<{ shiftUp?: boolean }>`
  position: relative;
  margin-bottom: 1rem;

  ${({ shiftUp }) =>
    shiftUp &&
    css`
      margin-top: -1.25rem;
    `}
`

const Actions = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0rem 0.5rem;
`

const getValidationMessage = (block: CaptureBlockType) => {
  if (block.validationText) return block.validationText

  if (block.name) {
    return `Please enter a value for ${block.name}`
  }

  return 'Please enter a value'
}

/**
 * Some blocks have complicated values, not just primitives. This validator covers some helpful cases:
 * arrays - if the array is empty, or if all it's values are "falsy" then the field is required
 * objects - if the object is empty, or if all it's values are "falsy" then the field is required (ProjectLocationCaptureBlock)
 * date range - both values must be present
 * signature - both name and signature url must be present
 */
const generalRequiredValidator =
  (block: CaptureBlockType) => (_: Rule, value: unknown) => {
    if (value === undefined || value === null) {
      return Promise.reject()
    }

    if (typeof value === 'string' && value.trim() === '') {
      return Promise.reject()
    }

    if (Array.isArray(value)) {
      if (value.filter(Boolean).length === 0) {
        return Promise.reject()
      }
    }

    if (typeof value === 'object') {
      if (Object.keys(value).length === 0) {
        return Promise.reject()
      }

      if (Object.values(value).filter(Boolean).length === 0) {
        return Promise.reject()
      }
    }

    if (block.type === 'dateTime' && block.captureRange) {
      const dateValue = value as RangePickerProps['value']

      if (!dateValue || dateValue.some((v) => !v)) {
        return Promise.reject()
      }
    }

    if (block.type === 'signature') {
      const signatureValue = value as SignatureData
      if (!signatureValue.url) {
        return Promise.reject()
      }
      if (!signatureValue.signee) {
        return Promise.reject()
      }
    }

    return Promise.resolve()
  }

const getCaptureBlock = (block: CaptureBlockType) => {
  const defaultRules: Rule[] = block.required
    ? [
        {
          required: true,
          message: getValidationMessage(block),
          validator: generalRequiredValidator(block),
        },
      ]
    : []

  return getBlockConfig(block.kind, block.type)?.renderBlock(
    block,
    defaultRules
  )
}

type Props = {
  block: CaptureBlockType
  blockGroupId: string
}

const CaptureBlock: React.FC<Props> = ({ block, blockGroupId }) => {
  const container = useRef<HTMLDivElement>(undefined)
  const { id, notes, attachments, platformActions, defects } = block
  const { renderOptions, config } = useTemplateRenderer()

  const [editing, setEditing] = useState(false)

  const startEditing = () => setEditing(true)
  const stopEditing = () => setEditing(false)

  const notesId = useMemo(() => getNotesId(id), [id])
  const captureBlock = useMemo(() => getCaptureBlock(block), [block])

  if (!captureBlock) return null

  const renderAttachmentsElementId = block.id + '-attachmentsDisplay'

  return (
    <CaptureBlockWrapper ref={container} shiftUp={block.type === 'action'}>
      {captureBlock}

      <DisplayAttachmentsWrapper id={renderAttachmentsElementId} />

      <DisplayFormNotes parentId={id} initialNotes={notes} />

      <DisplayFormPlatformActions
        parentId={id}
        initialActions={platformActions}
      />

      <DisplayFormDefects parentId={id} initialDefects={defects} />

      <NotesEditor
        notesId={notesId}
        notes={notes}
        editing={editing}
        stopEditing={stopEditing}
      />

      <Actions>
        <AddAnother type="BLOCK" item={block} parentId={blockGroupId} />

        {notes !== false && renderOptions.showNotes && (
          <CaptureNotes editing={editing} startEditing={startEditing} />
        )}

        {renderOptions.showActions && (
          <CapturePlatformActions
            parentId={id}
            actions={platformActions}
            block={block}
          />
        )}

        {renderOptions.showDefects && !config?.isPublic && (
          <CaptureDefects parentId={id} defects={defects} />
        )}

        {renderOptions.showAttachments && (
          <CaptureAttachments
            parentId={id}
            container={container}
            attachments={attachments}
            renderAttachmentsElementId={renderAttachmentsElementId}
          />
        )}
      </Actions>
    </CaptureBlockWrapper>
  )
}

export default CaptureBlock
